import React, { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  setUpTOTP,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchMFAPreference,
} from "aws-amplify/auth";
import { ShieldCheck, ShieldAlert, Smartphone, Copy, CheckCircle2, AlertCircle, X, Loader2, Brain } from "lucide-react";
import { getFreshToken } from "../api";

const API_URL = (process.env.REACT_APP_API_URL || "").replace(/^["']|["']$/g, "").trim().replace(/\/$/, "");
const card = { background: "#fff", border: "1px solid #eaeef4", borderRadius: "14px", padding: "24px", boxShadow: "0 1px 2px rgba(16,24,40,.04), 0 1px 3px rgba(16,24,40,.06)" };
const label = { display: "block", fontSize: "13px", fontWeight: 600, color: "#334155", marginBottom: "8px" };

export default function SecurityPage({ userEmail }) {
  const [loading, setLoading] = useState(true);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Setup flow
  const [setupUri, setSetupUri] = useState(null);
  const [secret, setSecret] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState(false);

  // AI conversation memory (R18.6) — default on, per-user off switch.
  const [memoryOn, setMemoryOn] = useState(null); // null = loading
  const [memoryBusy, setMemoryBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const t = await getFreshToken();
        const res = await fetch(`${API_URL}/ai/chat/settings`, { headers: { Authorization: `Bearer ${t}` } });
        if (res.ok) { const d = await res.json(); setMemoryOn(!!d.enabled); }
        else setMemoryOn(true);
      } catch (e) { setMemoryOn(true); }
    })();
  }, []);

  const toggleMemory = async () => {
    const next = !memoryOn;
    setMemoryBusy(true);
    try {
      const t = await getFreshToken();
      const res = await fetch(`${API_URL}/ai/chat/settings`, {
        method: "PUT", headers: { "Content-Type": "application/json", Authorization: `Bearer ${t}` },
        body: JSON.stringify({ enabled: next }),
      });
      if (res.ok) setMemoryOn(next);
    } catch (e) { /* ignore */ }
    finally { setMemoryBusy(false); }
  };

  const refreshStatus = async () => {
    try {
      const pref = await fetchMFAPreference();
      const enabled = (pref?.enabled || []).includes("TOTP") || pref?.preferred === "TOTP";
      setMfaEnabled(!!enabled);
    } catch (e) {
      // If we can't read preference, default to "not enabled" view.
      setMfaEnabled(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshStatus(); }, []);

  const beginSetup = async () => {
    setError(null); setSuccess(null); setBusy(true);
    try {
      const details = await setUpTOTP();
      const uri = details.getSetupUri("Boomerang", userEmail || "account").toString();
      setSetupUri(uri);
      setSecret(details.sharedSecret);
    } catch (e) {
      setError(e?.message || "Could not start MFA setup.");
    } finally {
      setBusy(false);
    }
  };

  const confirmSetup = async (e) => {
    e.preventDefault();
    setError(null); setSuccess(null); setBusy(true);
    try {
      await verifyTOTPSetup({ code: code.trim() });
      await updateMFAPreference({ totp: "PREFERRED" });
      setSuccess("Multi-factor authentication is now enabled. You'll be asked for a code at every sign-in.");
      setSetupUri(null); setSecret(""); setCode("");
      setMfaEnabled(true);
    } catch (e) {
      setError(e?.message || "That code didn't match. Check the time on your device and try again.");
    } finally {
      setBusy(false);
    }
  };

  const disableMfa = async () => {
    setError(null); setSuccess(null); setBusy(true);
    try {
      await updateMFAPreference({ totp: "DISABLED" });
      setSuccess("Multi-factor authentication has been turned off.");
      setMfaEnabled(false);
    } catch (e) {
      setError(e?.message || "Could not disable MFA.");
    } finally {
      setBusy(false);
    }
  };

  const copySecret = () => {
    navigator.clipboard?.writeText(secret).then(() => {
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div style={{ padding: "20px", maxWidth: "720px", margin: "0 auto" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "12px", borderBottom: "1px solid #eaeef4", paddingBottom: "18px", marginBottom: "28px" }}>
        <div style={{ width: "44px", height: "44px", borderRadius: "12px", background: "linear-gradient(135deg, #6366f1, #7c3aed)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 6px 16px rgba(99,102,241,.28)" }}>
          <ShieldCheck size={22} color="#fff" />
        </div>
        <div>
          <h1 style={{ margin: 0, fontSize: "24px", color: "#0f172a" }}>Security</h1>
          <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "14px" }}>Protect your account with multi-factor authentication</p>
        </div>
      </div>

      {error && <div style={{ padding: "12px", background: "#fee2e2", color: "#991b1b", borderRadius: "6px", marginBottom: "20px", border: "1px solid #f87171", display: "flex", alignItems: "center", gap: "8px" }}><AlertCircle size={16} /> {error}</div>}
      {success && <div style={{ padding: "12px", background: "#ecfdf5", color: "#0f766e", borderRadius: "6px", marginBottom: "20px", border: "1px solid #6ee7b7", display: "flex", alignItems: "center", gap: "8px" }}><CheckCircle2 size={16} /> {success}</div>}

      <div style={card}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
          <Smartphone size={20} color="#6366f1" />
          <h2 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>Authenticator App (TOTP)</h2>
        </div>
        <p style={{ color: "#475569", fontSize: "14px", lineHeight: 1.6, marginBottom: "20px" }}>
          Use an authenticator app like Google Authenticator, Microsoft Authenticator, or Authy to generate a
          6-digit code at each sign-in. This is the strongest, phishing-resistant option and works offline.
        </p>

        {loading ? (
          <div style={{ display: "flex", alignItems: "center", gap: "8px", color: "#64748b", fontSize: "14px" }}>
            <Loader2 size={16} className="animate-spin" /> Checking status…
          </div>
        ) : mfaEnabled ? (
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 14px", borderRadius: "999px", background: "#ecfdf5", color: "#0f766e", border: "1px solid #6ee7b7", fontSize: "13px", fontWeight: 600, marginBottom: "18px" }}>
              <ShieldCheck size={15} /> MFA is enabled
            </div>
            <div>
              <button onClick={disableMfa} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 18px", background: "#fff", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: "6px", fontWeight: 600, cursor: busy ? "not-allowed" : "pointer" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldAlert size={16} />} Turn off MFA
              </button>
            </div>
          </div>
        ) : !setupUri ? (
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 14px", borderRadius: "999px", background: "#fff7ed", color: "#9a3412", border: "1px solid #fed7aa", fontSize: "13px", fontWeight: 600, marginBottom: "18px" }}>
              <ShieldAlert size={15} /> MFA is not enabled
            </div>
            <div>
              <button onClick={beginSetup} disabled={busy} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 22px", background: busy ? "#cbd5e1" : "linear-gradient(135deg, #6366f1, #7c3aed)", color: "#fff", border: "none", borderRadius: "10px", fontWeight: 600, cursor: busy ? "not-allowed" : "pointer", boxShadow: busy ? "none" : "0 6px 16px rgba(99,102,241,.28)" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />} Enable MFA
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={confirmSetup}>
            <ol style={{ color: "#334155", fontSize: "14px", lineHeight: 1.7, paddingLeft: "18px", marginBottom: "18px" }}>
              <li>Scan this QR code with your authenticator app.</li>
              <li>Enter the 6-digit code it shows to confirm.</li>
            </ol>
            <div style={{ display: "flex", gap: "24px", flexWrap: "wrap", alignItems: "flex-start", marginBottom: "20px" }}>
              <div style={{ background: "#fff", padding: "12px", border: "1px solid #e2e8f0", borderRadius: "10px" }}>
                <QRCodeSVG value={setupUri} size={168} level="M" />
              </div>
              <div style={{ flex: 1, minWidth: "220px" }}>
                <label style={label}>Can't scan? Enter this key manually:</label>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "18px" }}>
                  <code style={{ flex: 1, fontSize: "13px", background: "#f1f5f9", padding: "10px 12px", borderRadius: "6px", wordBreak: "break-all", color: "#0f172a" }}>{secret}</code>
                  <button type="button" onClick={copySecret} title="Copy" style={{ padding: "10px", background: "#fff", border: "1px solid #e2e8f0", borderRadius: "10px", cursor: "pointer", color: copied ? "#16a34a" : "#475569" }}>
                    {copied ? <CheckCircle2 size={16} /> : <Copy size={16} />}
                  </button>
                </div>
                <label style={label}>6-digit code</label>
                <input
                  type="text" inputMode="numeric" autoComplete="one-time-code"
                  value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                  placeholder="000000"
                  style={{ width: "100%", padding: "12px 14px", border: "1px solid #e2e8f0", borderRadius: "10px", fontSize: "18px", letterSpacing: "4px", textAlign: "center", outline: "none" }}
                />
              </div>
            </div>
            <div style={{ display: "flex", gap: "12px" }}>
              <button type="button" onClick={() => { setSetupUri(null); setSecret(""); setCode(""); setError(null); }} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 20px", background: "#fff", color: "#475569", border: "1px solid #e2e8f0", borderRadius: "10px", fontWeight: 600, cursor: "pointer" }}>
                <X size={16} /> Cancel
              </button>
              <button type="submit" disabled={busy || code.length !== 6} style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "11px 22px", background: (busy || code.length !== 6) ? "#cbd5e1" : "linear-gradient(135deg, #6366f1, #7c3aed)", color: "#fff", border: "none", borderRadius: "10px", fontWeight: 600, cursor: (busy || code.length !== 6) ? "not-allowed" : "pointer", boxShadow: (busy || code.length !== 6) ? "none" : "0 6px 16px rgba(99,102,241,.28)" }}>
                {busy ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />} Verify &amp; Enable
              </button>
            </div>
          </form>
        )}
      </div>

      {/* AI conversation memory (Campaign Projects R18.6) */}
      <div style={{ ...card, marginTop: "20px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "8px" }}>
          <Brain size={20} color="#6366f1" />
          <h2 style={{ margin: 0, fontSize: "18px", color: "#0f172a" }}>AI conversation memory</h2>
        </div>
        <p style={{ color: "#475569", fontSize: "14px", lineHeight: 1.6, marginBottom: "20px" }}>
          When on, AI Insights remembers each project's conversation so you can pick up where you left off,
          and gets smarter about that project over time. We store only your questions, the AI's answer text,
          and chart definitions — never the underlying rows — and personal data in that text is masked. Each
          project keeps its own separate history; nothing is shared across projects. You can clear a project's
          history anytime from the AI Insights page.
        </p>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "8px 14px", borderRadius: "999px",
            background: memoryOn === false ? "#fff7ed" : "#ecfdf5", color: memoryOn === false ? "#9a3412" : "#0f766e",
            border: `1px solid ${memoryOn === false ? "#fed7aa" : "#6ee7b7"}`, fontSize: "13px", fontWeight: 600 }}>
            {memoryOn === null ? <Loader2 size={15} className="animate-spin" /> : <Brain size={15} />}
            {memoryOn === null ? "Checking…" : memoryOn ? "Memory is on" : "Memory is off"}
          </div>
          <button onClick={toggleMemory} disabled={memoryBusy || memoryOn === null}
            style={{ display: "inline-flex", alignItems: "center", gap: "8px", padding: "10px 18px",
              background: "#fff", color: "#334155", border: "1px solid #e2e8f0", borderRadius: "10px",
              fontWeight: 600, cursor: (memoryBusy || memoryOn === null) ? "not-allowed" : "pointer" }}>
            {memoryBusy ? <Loader2 size={16} className="animate-spin" /> : null}
            {memoryOn ? "Turn off" : "Turn on"}
          </button>
        </div>
      </div>
    </div>
  );
}
